import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { POST_AUTH_REDIRECT_KEY, sanitizeRedirect } from "@/lib/authRedirect";
import { takeFollowIntent } from "@/lib/followIntent";
import { supabase } from "@/integrations/supabase/client";
import { attachFollowAttribution, sendEvent, takeFollowSource } from "@/lib/firstPartyAnalytics";
import { isDemoRoute } from "@/lib/demoSafety";

const PostAuthRedirect = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || !user || isDemoRoute(location.pathname)) return;

    const intent = takeFollowIntent();
    if (intent) {
      const source = takeFollowSource(intent.nonce, intent.athleteId);
      const consentAt = new Date().toISOString();
      const profileChanges = {
        ...(intent.firstName ? { first_name: intent.firstName, display_name: intent.firstName } : {}),
        ...(intent.newsletterConsent ? { newsletter_opt_in: true, newsletter_consented_at: consentAt } : {}),
      };
      void Promise.all([
        supabase.from("athlete_favorites").upsert({
          user_id: user.id,
          athlete_id: intent.athleteId,
          updates_opt_in: true,
          updates_consented_at: consentAt,
        }),
        supabase.from("profiles").update(profileChanges).eq("id", user.id),
      ]).then(([{ error }]) => {
        if (error) return;
        sendEvent("follow_complete", { athleteId: intent.athleteId, metadata: { newsletter_opt_in: intent.newsletterConsent } });
        attachFollowAttribution(intent.athleteId, source);
      });
    }

    const redirectTo = sanitizeRedirect(window.localStorage.getItem(POST_AUTH_REDIRECT_KEY));
    if (!redirectTo) return;

    const currentPath = `${location.pathname}${location.search}`;
    window.localStorage.removeItem(POST_AUTH_REDIRECT_KEY);

    if (redirectTo !== currentPath) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, user, location.pathname, location.search, navigate]);

  return null;
};

export default PostAuthRedirect;